import classnames from "classnames";
import { Form, Formik } from "formik";
import React, { Fragment, useState } from "react";

import { FormInfoProvider } from "@utils/forms/formInfo.provider";
import { isDefined } from "@utils/helpers";
import * as validationRules from "@utils/validations";

import { WishlistModel } from "@redux/modules/api/data/wishlist/@types/wishlist.model";
import { isFull } from "@redux/modules/api/data/wishlist/wishlist.utils";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { TextField } from "@components/Form/FormFields/TextField/textField.component";

import { MAX_WISHLISTS } from "../wishlist.config";
import "./WishlistActionable.scss";

export interface WishListActionableProps {
    formId: string;
    wishlists: WishlistModel[];
    loading: boolean;
    currentWishlistId?: string;
    mode: WishlistActionableMode;
    submit: ({
        wishlistId,
        wishlistName,
    }: {
        wishlistId?: string;
        wishlistName?: string;
    }) => void;
}

export enum WishlistActionableMode {
    ADD,
    MOVE,
}

enum SelectMode {
    EXISTING,
    NEW,
}

const WishListActionable = ({
    formId,
    wishlists,
    loading,
    currentWishlistId,
    mode,
    submit,
}: WishListActionableProps) => {
    const { t } = useGranite();
    const [formValue, setFormValue] = useState<{
        value?: string;
        selectMode: SelectMode;
    }>({
        value: currentWishlistId,
        selectMode: SelectMode.EXISTING,
    });

    const allowCreateNew = wishlists.length < MAX_WISHLISTS;

    const isDisabled = (wishlist: WishlistModel) =>
        (mode === WishlistActionableMode.MOVE &&
            currentWishlistId === wishlist.id) ||
        isFull(wishlist);

    const trapFocus = (element: HTMLElement | null, wishlistId: string) => {
        if (
            !isDefined(element) ||
            formValue.selectMode !== SelectMode.EXISTING ||
            formValue.value !== wishlistId ||
            document.activeElement === element
        ) {
            return;
        }

        // Move focus to element inside actionable
        element.focus();
        setTimeout(() => element.blur());
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center">
                <i className="las la-3x la-spinner la-pulse"></i>
            </div>
        );
    }

    return (
        <FormInfoProvider formId={formId} withNeverbounce={false}>
            {() => (
                <Formik
                    initialValues={{
                        "wishlist-id": "",
                        "wishlist-name": "",
                    }}
                    onSubmit={values => {
                        submit(
                            formValue.selectMode === SelectMode.EXISTING
                                ? { wishlistId: formValue.value }
                                : { wishlistName: values["wishlist-name"] },
                        );
                    }}
                >
                    <Form id={formId} className={"d-flex flex-column mt-2"}>
                        {wishlists.map(wishlist => (
                            <Fragment key={wishlist.id}>
                                <label
                                    className={
                                        "wishlist-actionable-item d-flex"
                                    }
                                >
                                    <div
                                        className={
                                            "wishlist-actionable-item__radio"
                                        }
                                    >
                                        <input
                                            name={"wishlist-id"}
                                            type="radio"
                                            disabled={isDisabled(wishlist)}
                                            checked={
                                                formValue.selectMode ===
                                                    SelectMode.EXISTING &&
                                                formValue.value === wishlist.id
                                            }
                                            onChange={() =>
                                                setFormValue({
                                                    value: wishlist.id,
                                                    selectMode:
                                                        SelectMode.EXISTING,
                                                })
                                            }
                                            ref={node =>
                                                trapFocus(node, wishlist.id)
                                            }
                                        />
                                    </div>
                                    <div
                                        className={classnames(
                                            "wishlist-actionable-item__info",
                                            isDisabled(wishlist) &&
                                                "color-light-gray",
                                        )}
                                    >
                                        <h5 className="wishlist-actionable-item__title">
                                            {wishlist.name}
                                        </h5>
                                        <p className="small">
                                            {t(
                                                "wishlist.productCount",
                                                (wishlist.products || [])
                                                    .length,
                                            )}
                                        </p>
                                    </div>
                                </label>
                            </Fragment>
                        ))}
                        {allowCreateNew && (
                            <label className="wishlist-actionable-item wishlist-actionable-item--new">
                                <div className="wishlist-actionable-item__radio">
                                    <input
                                        type="radio"
                                        name={"wishlist-id"}
                                        checked={
                                            formValue.selectMode ===
                                            SelectMode.NEW
                                        }
                                        onChange={() =>
                                            setFormValue({
                                                value: undefined,
                                                selectMode: SelectMode.NEW,
                                            })
                                        }
                                    />
                                </div>
                                <div
                                    onClick={() =>
                                        setFormValue({
                                            value: undefined,
                                            selectMode: SelectMode.NEW,
                                        })
                                    }
                                >
                                    <TextField
                                        label={t("wishlist.newWishlist")}
                                        name="wishlist-name"
                                        validationRule={
                                            validationRules.optionalWishlistName
                                        }
                                        placeholder={t(
                                            "wishlist.name.placeholder",
                                        )}
                                    />
                                </div>
                            </label>
                        )}
                    </Form>
                </Formik>
            )}
        </FormInfoProvider>
    );
};

export { WishListActionable };
